// src/AuthContext.tsx
import React, { createContext, useEffect, useState } from 'react';
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firebase-firestore";
import { Collections } from '../types/collection';
import { User } from '../interfaces/user.interface';
import { captureException } from "@sentry/react";

interface UserContextProps {
    user?: User | null;
    reloadUserData: () => void;
}

export const UserContext = createContext<UserContextProps>({
    user: null,
    reloadUserData: () => { }
});

export const useUserContext = (): UserContextProps => {
    const context = React.useContext(UserContext);
    if (!context) {
        throw new Error('User context must be used within a provider');
    }
    return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [triggerData, setTriggerData] = useState<number>(new Date().getTime());
    
    useEffect(() => {
        async function getUserSessions() {
            try {
                const userId = await firebase.auth().currentUser?.uid;
                if (!userId) return;
                await firebase.firestore()
                    .collection(Collections.Users).doc(userId)
                    .onSnapshot(async (snapshot) => {
                        if (!snapshot.exists) {
                            setUser(null)
                            return;
                        }
                        const userData = await snapshot.data() as User;
                        setUser(userData);
                    })
            } catch (error) {
                captureException(error)
            }
        }
        getUserSessions();
    }, [triggerData])

    const triggerFetch = React.useCallback(() => {
        setTriggerData(new Date().getTime())
    }, [])

    return (
        <UserContext.Provider
            value={{
                user,
                reloadUserData: triggerFetch
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
